import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataViewLayoutOptions } from "primereact/dataview";
import { ProgressSpinner } from "primereact/progressspinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { classNames } from "primereact/utils";
import MediaSection from "./MediaSection";

// Years dropdown options
// const pubDates = [
//   { name: "2024", code: "24" },
//   { name: "2025", code: "25" },
// ];

function getPubDates(startYear = 2024) {
  const currentYear = new Date().getFullYear();
  const pubDates = [];

  for (let year = startYear; year <= currentYear; year++) {
    pubDates.push({
      name: year.toString(),
      code: year.toString().slice(-2),
    });
  }

  return pubDates;
}
const pubDates = getPubDates();

const EventPublicationsPage = () => {
  const navigate = useNavigate();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeIndex, setActiveIndex] = useState(1); // Default to Publications tab
  const [layout, setLayout] = useState("grid");

  // Fetch publications - the key change is using refetchOnMount: "always"
  const {
    data: mediaItems = [],
    isLoading: isLoadingPublications,
    isError: isPublicationsError,
    error: publicationsError,
  } = useQuery({
    queryKey: ["mediaItems"],
    queryFn: async () => {
      const response = await axios.get("api/resources/publications");
      return response.data || [];
    },
    refetchOnMount: "always", // Always refetch when component mounts - THIS IS THE KEY FIX
  });

  // Apply filters
  const filteredPublication = React.useMemo(() => {
    let filtered = [...(mediaItems || [])];

    filtered = filtered.map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    }));

    filtered = filtered.slice(0, 12);

    // Apply date filter
    if (selectedDate) {
      const selectedYear = selectedDate.name;
      filtered = filtered.filter((item) => {
        if (!item.date) return false;
        const year = new Date(item.date).getFullYear().toString();
        return year === selectedYear;
      });
    }

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.title && item.title.toLowerCase().includes(searchLower)) ||
          (item.author && item.author.toLowerCase().includes(searchLower)) ||
          (item.date && item.date.toLowerCase().includes(searchLower))
      );
    }

    const sortedRecords = filtered.sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });

    return sortedRecords;
  }, [mediaItems, selectedDate, searchQuery]);

  const {
    data: galleries = [],
    isLoading: isLoadingGalleries,
    isError: isGalleriesError,
    error: galleriesError,
  } = useQuery({
    queryKey: ["galleries"],
    queryFn: async () => {
      const response = await axios.get("api/resources/galleries");
      const sortedRecords = response.data.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });

      return sortedRecords.slice(0, 3) || [];
    },
    refetchOnMount: "always", // Always refetch when component mounts
  });

  // Determine if any data is loading
  const isLoading = isLoadingPublications || isLoadingGalleries;

  // Handle errors
  const isError = isPublicationsError || isGalleriesError;
  const errorMessage = publicationsError?.message || galleriesError?.message;

  // Handle clear filters
  const clearFilters = () => {
    setSelectedDate(null);
    setSearchQuery("");
  };

  const renderListItem = (publication) => {
    return (
      <div className="col-12" key={publication.id}>
        <div className="flex flex-column xl:flex-row xl:align-items-start md:p-4 gap-4 surface-border border-2 border-round md:m-2">
          <img
            className="w-[160px] h-[160px] object-cover shadow-2 block mx-auto border-round"
            src={publication.image}
            alt={publication.title}
          />
          <div className="flex flex-1 flex-col  gap-3">
            <div className="min-h-[64px] text-lg font-bold w-full px-4">
              {publication.link && (
                <Button
                  icon="pi pi-external-link"
                  label={publication.title}
                  onClick={() => window.open(publication.link, "_blank")}
                  className="w-full p-button-primary line-clamp-2 justify-center text-center w-full"
                />
              )}
            </div>
            <div className="text-lg mt-2 text-color-secondary w-full justify-center text-center">
              {publication.date ? (
                // new Date(publication.date).toLocaleDateString()
                publication.date
              ) : (
                <span>&nbsp;</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGridItem = (publication) => {
    return (
      <div className="flex flex-col items-center gap-3">
        <Card
          key={publication.id}
          className="card-no-top-padding  hover:shadow-lg w-full h-[380px] flex flex-col justify-between"
        >
          {/* Image */}
          <div className="w-full flex justify-center">
            <img
              className="w-full  h-[200px] object-cover border-round shadow-2"
              src={publication.image}
              alt={publication.title}
            />
          </div>

          {/* Title */}
          <div className="text-lg font-bold text-center w-full mt-2 w-full min-h-[60px] line-clamp-3">
            <Button
              icon="pi pi-external-link"
              label={publication.title}
              onClick={() => window.open(publication.link, "_blank")}
              className="p-button-primary pt-4 line-clamp-3"
            />
          </div>

          <div className="w-full text-center mt-2 space-y-1 min-h-[40px]">
            <div className="text-lg mt-4 text-color-secondary h-[20px]">
              {publication.date ? (
                // new Date(publication.date).toLocaleDateString()
                publication.date
              ) : (
                <span>&nbsp;</span>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-white py-48 md:py-24">
      <div className="container mx-auto px-8 md:px-20 md:flex-row items-start justify-between">
        {/* Summary Section */}
        <div className="text-black md:py-8">
          <p className="text-lg md:text-2xl">
            Explore slide decks and event recordings from the AIRCHECK
            community. This page serves as a growing repository of materials
            designed to provide access to scientific publications that cite or
            build upon AIRCHECK data.
          </p>
        </div>

        <div className="flex flex-col py-2 md:flex-row md:gap-4 md:items-start">
          {/* Left Side - Filter Section */}
          <div className="w-full md:w-80 md:flex-shrink-0 bg-white/90">
            <div className="bg-white rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">
                Filters
              </h2>

              {/* Search Bar */}
              <div className="my-2">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <i className="pi pi-search text-[#06002e] text-2xl" />
                  </div>

                  <span className="p-float-label w-full">
                    <InputText
                      id="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      // className="custom-input px-2 py-2 text-black transition duration-200 w-full"
                      className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                    />
                    <label
                      htmlFor="search"
                      className="w-full font-medium !transition-all !duration-200"
                    >
                      Search For Publication
                    </label>
                  </span>
                </div>
              </div>

              {/* Date Dropdown */}
              <div className="my-8 ml-8">
                <label className="block text-md font-medium text-gray-700 mb-1">
                  Date (Year)
                </label>

                <Dropdown
                  value={selectedDate}
                  options={pubDates}
                  onChange={(e) => setSelectedDate(e.value)}
                  optionLabel="name"
                  placeholder="Select Publication Year"
                  className="w-full p-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white shadow-sm transition-all duration-200"
                />
              </div>

              <div className="flex-grow text-center">
                <Button
                  label="Clear Filters"
                  icon="pi pi-filter-slash"
                  onClick={clearFilters}
                  className="text-[#06002e] font-bold rounded-xl px-4 py-2 border-2 border-[#f36548] hover:bg-[#f36548] hover:text-black transition duration-200"
                />
              </div>
            </div>
          </div>

          {/* Middle - Gallery and Publications */}
          <div className="w-full py-4 md:flex-1 md:pl-4">
            {isLoading ? (
              <div className="flex justify-center p-4">
                <ProgressSpinner style={{ width: "150px", height: "150px" }} />
              </div>
            ) : isError ? (
              <div className="text-center p-4 text-red-700">
                <p>Error loading publications. Please try again.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg space-y-4 shadow-md">
                <Accordion
                  // activeIndex={activeIndex}
                  multiple
                  activeIndex={[0, 1]}
                  // onTabChange={(e) => setActiveIndex(e.index)}
                >
                  <AccordionTab
                    header="Gallery"
                    headerClassName="custom-accordion-header"
                  >
                    <div className="p-4">
                      <h3 className="text-xl font-semibold mb-4">
                        Slide decks and video archives from AIRCHECK workshops,
                        hackathons, and webinars.
                      </h3>
                      <MediaSection filteredMedia={galleries} />
                      <div className="flex-grow pt-4 text-center">
                        <Button
                          label="Explore All Events"
                          icon="pi pi-arrow-right"
                          className="three-d-button"
                          onClick={() => navigate("/media")}
                          // className="text-[#06002e] font-bold rounded-xl px-4 py-2 border-2 border-[#f36548] hover:bg-[#f36548] hover:text-black transition duration-200"
                        />
                      </div>
                    </div>
                  </AccordionTab>

                  <AccordionTab
                    header="Publications"
                    headerClassName="custom-accordion-header"
                  >
                    <div className="p-4">
                      <h3 className="text-xl font-semibold mb-4">
                        This list serves as a growing repository of scientific
                        publications that cite or build upon AIRCHECK data.
                      </h3>
                      {!filteredPublication ||
                      filteredPublication.length === 0 ? (
                        <div className="text-center text-lg text-gray-600 py-8">
                          <h3 className="font-semibold mb-2">
                            No records found
                          </h3>
                          <p>
                            Please try adjusting your search parameters or
                            filters.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-content-end mb-4">
                            <DataViewLayoutOptions
                              layout={layout}
                              onChange={(e) => setLayout(e.value)}
                              className="custom-dataview-options"
                            />
                          </div>

                          {/* Manual implementation of grid/list view */}
                          {layout === "grid" ? (
                            <div className="flex flex-wrap -mx-2">
                              {filteredPublication.map((item, index) => (
                                <div
                                  className="w-full sm:w-1/2 lg:w-1/3 px-2 py-2"
                                  key={item.id || `pub-${index}`}
                                >
                                  {renderGridItem(item)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div>
                              {filteredPublication.map((item, index) => (
                                <React.Fragment key={item.id || `pub-${index}`}>
                                  {renderListItem(item)}
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPublicationsPage;
